// src/app/create-quote.jsx
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { MdAdd, MdRemove } from 'react-icons/md';
import API_BASE_URL from '../apiConfig.js';

// --- helpers ---
const getToken = () => {
  try { return localStorage.getItem('userToken'); } catch { return null; }
};

export default function CreateQuote() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // customer selection
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [customerName, setCustomerName] = useState('');

  // quote data
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [notes, setNotes] = useState('');
  const [isNotesVisible, setIsNotesVisible] = useState(false);

  // add-customer inline form
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: '',
    notes: '',
  });

  // preview overlay
  const [previewOpen, setPreviewOpen] = useState(false);

  // user permissions
  const [userPermissions, setUserPermissions] = useState(null);

  // Custom Modal Component
  const [customModal, setCustomModal] = useState({
    open: false,
    title: '',
    message: '',
    buttons: []
  });

  // Email state for sending quotes
  const [sendingEmail, setSendingEmail] = useState(false);
  const [lastCreatedQuoteId, setLastCreatedQuoteId] = useState(null);
  const [previewQuoteData, setPreviewQuoteData] = useState({});
  const [businessInfo, setBusinessInfo] = useState({});
  const [businessLogoUrl, setBusinessLogoUrl] = useState(null); // Add business logo URL state
  

  const showCustomModal = (title, message, buttons) => {
    setCustomModal({ open: true, title, message, buttons });
  };

  const closeCustomModal = () => {
    setCustomModal({ open: false, title: '', message: '', buttons: [] });
  };

  useEffect(() => {
    const pTitle = searchParams.get('title');
    const pDesc = searchParams.get('description');
    const pPrice = searchParams.get('price');
    const pCustomerName = searchParams.get('customerName');

    if (pTitle) setTitle(pTitle);
    if (pDesc) setDescription(pDesc);
    if (pPrice) setPrice(String(pPrice));
    if (pCustomerName) {
      setCustomerName(pCustomerName);
      setSearchQuery(pCustomerName);
      setSelectedCustomer(pCustomerName);
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const token = getToken();
        const { data } = await axios.get(`${API_BASE_URL}/api/customers`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        const list = Array.isArray(data) ? data : data?.customers ?? [];
        setCustomers(list);

        const pCustomerName = searchParams.get('customerName');
        if (pCustomerName && list.some((c) => c.name === pCustomerName)) {
          setFilteredCustomers([]);
        }
      } catch (err) {
        console.error('Error fetching customers:', err);
      }
    };
    fetchCustomers();
  }, [searchParams]);

  useEffect(() => {
    const loadUserPermissions = () => {
      try {
        const permissions = localStorage.getItem('userPermissions');
        if (permissions) {
          const parsedPermissions = JSON.parse(permissions);
          setUserPermissions(parsedPermissions);
          
          const user_type = parsedPermissions?.user_type;
          const quotePerm = parsedPermissions?.quote_perm;
          
          if (user_type !== 'Owner' && user_type !== 'Admin' && quotePerm !== 'write') {
            alert("You don't have permission to create quotes. Please contact your account administrator.");
            navigate('/'); 
            return;
          }
        }
      } catch (error) {
        console.error('Error loading user permissions:', error);
      }
    };
    loadUserPermissions();
  }, [navigate]);

  const handleSearch = (q) => {
    setSearchQuery(q);
    if (!q.trim()) {
      setFilteredCustomers([]);
      return;
    }
    const t = q.toLowerCase();
    const matches = customers.filter((c) => (c?.name ?? '').toLowerCase().includes(t));
    setFilteredCustomers(matches);
  };

  const handleAddCustomer = async () => {
    try {
      const token = getToken();
      
      // Clean the customer data before sending - convert empty email to null
      const customerPayload = {
        ...newCustomer,
        email: newCustomer.email?.trim() || null,  // Convert empty string to null
        notes: newCustomer.notes || '',
      };
      
      const { data } = await axios.post(`${API_BASE_URL}/api/customers/returnId`, customerPayload, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      const added = data;
      const updated = [...customers, added];
      setCustomers(updated);

      setCustomerName(newCustomer.name);
      setSearchQuery(newCustomer.name);
      setSelectedCustomer(newCustomer.name);
      setFilteredCustomers([]);
      setAddingCustomer(false);
      
      setNewCustomer({
        name: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        phone: '',
        email: '',
        notes: '',
      });
      
      showCustomModal(
        'Customer Added Successfully! ✅',
        `${added.name} has been added to your customer list and is now selected.`,
        [{ text: 'Great!', style: 'primary', onPress: closeCustomModal }]
      );
    } catch (error) {
      console.error('Error adding customer:', error);
      let errorMessage = 'Failed to add customer. Please try again.';
      if (error.response?.data?.detail) {
        errorMessage = `Error: ${error.response.data.detail}`;
      } else if (error.response?.data?.message) {
        errorMessage = `Error: ${error.response.data.message}`;
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid customer data. Please check all fields.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Permission denied. You do not have access to create customers.';
      } else if (error.response?.status === 422) {
        errorMessage = 'Invalid customer data format. Please check all required fields are filled.';
      } else if (error.message) {
        errorMessage = `Network error: ${error.message}`;
      }
      
      showCustomModal(
        'Error',
        errorMessage,
        [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
      );
    }
  };

  const saveQuote = async () => {
    const chosen = customers.find(
      (c) => (c?.name ?? '').trim().toLowerCase() === (selectedCustomer || searchQuery).trim().toLowerCase()
    );
    if (!chosen) {
      showCustomModal(
        'Error',
        'Please select a valid customer from the list or add a new one.',
        [{ text: 'OK', style: 'primary', onPress: closeCustomModal }]
      );
      return;
    }

    if (!title?.trim()) {
      showCustomModal(
        'Error',
        'Title is required. Please enter a title for the quote.',
        [{ text: 'OK', style: 'primary', onPress: closeCustomModal }]
      );
      return;
    }

    let cleanedPrice = price?.trim() || '';
    if (cleanedPrice === '' || isNaN(parseFloat(cleanedPrice))) {
      cleanedPrice = '0.00';
    } else {
      const numPrice = parseFloat(cleanedPrice);
      cleanedPrice = numPrice.toFixed(2);
    }

    const newQuote = {
      customer: chosen.name,
      customerId: chosen.id,
      title: title.trim(),
      description: description?.trim() || '',
      price: cleanedPrice,
      notes: notes?.trim() || '',
    };

    const paramId = searchParams.get('id');
    if (paramId) newQuote.id = paramId;

    const quotePreviewData = {
      customerName: chosen.name,
      title: title.trim(),
      description: description?.trim() || '',
      price: cleanedPrice,
      notes: notes?.trim() || '',
    };

    try {
      const token = getToken();
      const response = await axios.post(`${API_BASE_URL}/api/quotes`, newQuote, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      const quoteId = response.data?.id;
      const businessData = response.data?.businessInfo;

      if (businessData) {
        setBusinessInfo(businessData);
        // Set logo preview if exists
        if (businessData.businessLogo) {
          setBusinessLogoUrl(`${API_BASE_URL}/api/uploads/${businessData.businessLogo}`);
        }
      }

      if (!quoteId) {
        showCustomModal(
          'Warning',
          'Quote was created but ID was not returned. Please try emailing from the quotes list.',
          [{ text: 'OK', style: 'primary', onPress: closeCustomModal }]
        );
        setTitle('');
        setDescription('');
        setPrice('');
        setNotes('');
        setSearchQuery('');
        setSelectedCustomer('');
        setCustomerName('');
        setFilteredCustomers([]);
        setIsNotesVisible(false);
        setAddingCustomer(false);
        navigate('/quotes', { replace: true });
        return;
      }

      setLastCreatedQuoteId(quoteId);
      setPreviewQuoteData(quotePreviewData);

      setTitle('');
      setDescription('');
      setPrice('');
      setNotes('');
      setSearchQuery('');
      setSelectedCustomer('');
      setCustomerName('');
      setFilteredCustomers([]);
      setIsNotesVisible(false);
      setAddingCustomer(false);

      const emailQuotePDF = async () => {
        if (sendingEmail) return;
        
        if (!chosen?.email) {
          showCustomModal(
            'Email Not Available ⚠️',
            `Customer ${chosen?.name || quotePreviewData.customerName} does not have an email address on file. Please update customer information to enable emailing.`,
            [{ text: 'OK', style: 'secondary', onPress: closeCustomModal }]
          );
          return;
        }

        try {
          setSendingEmail(true);
          
          showCustomModal(
            'Quote Created Successfully! 🎉',
            'Your quote has been created. What would you like to do next?',
            [
              {
                text: 'Sending…',
                style: 'primary',
                disabled: true,
                onPress: () => {}
              },
              {
                text: '👁️ Preview Quote',
                style: 'secondary',
                onPress: () => {
                  closeCustomModal();
                  setPreviewOpen(true);
                }
              },
              {
                text: '💾 Save/Print PDF',
                style: 'default',
                onPress: async () => {
                  await generateAndSharePDF(quotePreviewData, quoteId, businessData);
                },
              },
              {
                text: '📋 View Quotes',
                style: 'default',
                onPress: () => {
                  closeCustomModal();
                  navigate('/quotes', { replace: true });
                }
              }
            ]
          );

          const emailResponse = await axios.post(`${API_BASE_URL}/api/quotes/${quoteId}/email`, {}, {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          });

          if (emailResponse.data && emailResponse.data.success) {
            showCustomModal(
              'Email Sent Successfully! ✅',
              `Quote has been emailed as PDF to ${chosen.email}`,
              [{ text: 'Great!', style: 'primary', onPress: closeCustomModal }]
            );
          } else {
            showCustomModal(
              'Email Status Unclear ⚠️',
              emailResponse.data?.message || 'Email may have been sent, but status is unclear.',
              [{ text: 'OK', style: 'secondary', onPress: closeCustomModal }]
            );
          }
        } catch (emailError) {
          console.error('Error sending quote email:', emailError);
          let errorMessage = 'Failed to send email. Please try again.';
          
          if (emailError.response?.status === 400) {
            errorMessage = `Customer email issue: ${chosen.email ? 'Email may be invalid' : 'No email address on file'}. Please update customer information.`;
          } else if (emailError.response?.status === 404) {
            errorMessage = 'Quote not found. Please try creating the quote again.';
          } else if (emailError.response?.data?.detail) {
            errorMessage = emailError.response.data.detail;
          } else if (emailError.response?.data?.message) {
            errorMessage = emailError.response.data.message;
          } else if (emailError.message) {
            errorMessage = `Network error: ${emailError.message}`;
          }
          
          showCustomModal(
            'Email Failed ❌',
            errorMessage,
            [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
          );
        } finally {
          setSendingEmail(false);
        }
      };

      showCustomModal(
        'Quote Created Successfully! 🎉',
        'Your quote has been created. What would you like to do next?',
        [
          {
            text: '📧 Email PDF to Customer',
            style: 'primary',
            onPress: emailQuotePDF
          },
          {
            text: '👁️ Preview Quote',
            style: 'secondary',
            onPress: () => {
              closeCustomModal();
              setPreviewOpen(true);
            }
          },
          {
            text: '💾 Save/Print PDF',
            style: 'default',
            onPress: async () => {
              await generateAndSharePDF(quotePreviewData, quoteId, businessData);
            },
          },
          {
            text: '📋 View Quotes',
            style: 'default',
            onPress: () => {
              closeCustomModal();
              navigate('/quotes', { replace: true });
            }
          }
        ]
      );
      
    } catch (error) {
      console.error('Error saving quote:', error);
      let errorMessage = 'Failed to save quote.';
      if (error.response?.data?.detail) {
        errorMessage = `Error: ${error.response.data.detail}`;
      } else if (error.response?.data?.message) {
        errorMessage = `Error: ${error.response.data.message}`;
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid quote data. Please check all fields.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Permission denied. You do not have access to create quotes.';
      } else if (error.message) {
        errorMessage = `Network error: ${error.message}`;
      }
      
      showCustomModal(
        'Error',
        errorMessage,
        [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
      );
    }
  };

  // Function to generate PDF and trigger browser download - matching create-invoice.jsx
const generateAndSharePDF = async (quoteDataToUse = null, quoteIdToUse = null, businessDataToUse = null) => {
  try {
    // Use passed data or fall back to state
    const dataToUse = quoteDataToUse || previewQuoteData;
    const idToUse = quoteIdToUse || lastCreatedQuoteId;
    const businessToUse = businessDataToUse || businessInfo;
    const logoToUse = businessDataToUse?.businessLogo 
      ? `${API_BASE_URL}/api/uploads/${businessDataToUse.businessLogo}` 
      : businessLogoUrl;
    
    // Get customer data
    const customer = customers.find(c => c.name === dataToUse.customerName);
    
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
          .header { display: flex; justify-content: space-between; margin-bottom: 30px; border-bottom: 2px solid #2563eb; padding-bottom: 20px; }
          .logo-container { margin-bottom: 10px; min-height: 85px; }
          .business-logo { max-width: 150px; max-height: 80px; object-fit: contain; }
          .company-name { font-size: 24px; font-weight: bold; color: #2563eb; margin-bottom: 10px; }
          .company-details { font-size: 13px; color: #666; line-height: 1.6; }
          .quote-title { font-size: 28px; font-weight: bold; color: #2563eb; }
          .quote-details { font-size: 13px; color: #666; margin-top: 8px; }
          .section-title { font-size: 18px; font-weight: bold; margin: 30px 0 15px 0; padding-bottom: 5px; border-bottom: 2px solid #e5e7eb; }
          .customer-info { background: #f8fafc; padding: 20px; border-radius: 8px; border-left: 4px solid #2563eb; }
          .service-item { border: 1px solid #e5e7eb; padding: 20px; border-radius: 8px; margin: 20px 0; }
          .pricing-section { background: #f8fafc; padding: 20px; border: 2px solid #2563eb; border-radius: 8px; margin: 30px 0; }
          .price-row { display: flex; justify-content: space-between; padding: 15px 0; border-bottom: 1px solid #e5e7eb; }
          .total-row { border-top: 2px solid #2563eb; border-bottom: none; font-size: 20px; font-weight: bold; margin-top: 10px; }
          .terms-section { background: #fefce8; padding: 20px; border-radius: 8px; border-left: 4px solid #eab308; margin: 30px 0; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 2px solid #e5e7eb; text-align: center; font-size: 13px; color: #666; }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="logo-container">
              ${logoToUse ? `<img src="${logoToUse}" alt="Business Logo" class="business-logo" />` : ''}
            </div>
            <div class="company-name">${businessToUse?.businessName || 'Your Business Name'}</div>
            <div class="company-details">
              ${businessToUse?.firstName || businessToUse?.lastName ? `${[businessToUse.firstName, businessToUse.lastName].filter(Boolean).join(' ')}<br>` : ''}
              ${businessToUse?.address || ''}<br>
              ${businessToUse?.city && businessToUse?.state ? `${businessToUse.city}, ${businessToUse.state} ${businessToUse.zip || ''}` : 'City, State'}<br>
              Phone: ${businessToUse?.phone || ''}<br>
              Email: ${businessToUse?.email || ''}
            </div>
          </div>
          <div style="text-align: right;">
            <div class="quote-title">QUOTE</div>
            <div class="quote-details">
              Quote #: Q-${idToUse || 'XXXX'}<br>
              Date: ${new Date().toLocaleDateString()}<br>
              Valid Until: ${new Date(Date.now() + 30*24*60*60*1000).toLocaleDateString()}
            </div>
          </div>
        </div>
        
        <div class="section-title">Quote For:</div>
        <div class="customer-info">
          <strong>${dataToUse.customerName || 'Customer Name'}</strong><br>
          ${customer?.address || ''}<br>
          ${customer?.city && customer?.state ? `${customer.city}, ${customer.state} ${customer.zip || ''}` : ''}<br>
          ${customer?.phone ? `Phone: ${customer.phone}` : ''}<br>
          ${customer?.email ? `Email: ${customer.email}` : ''}
        </div>
        
        <div class="section-title">Services</div>
        <div class="service-item">
          <strong style="font-size: 18px;">${dataToUse.title || 'Service Title'}</strong><br><br>
          ${dataToUse.description || 'No description provided'}
        </div>
        
        <div class="pricing-section">
          <div class="price-row">
            <span>Service Total:</span>
            <span><strong>$${parseFloat(dataToUse.price || 0).toFixed(2)}</strong></span>
          </div>
          <div class="price-row total-row">
            <span>Total Amount:</span>
            <span>$${parseFloat(dataToUse.price || 0).toFixed(2)}</span>
          </div>
        </div>
        
        <div class="terms-section">
          <strong>Quote Terms & Conditions</strong><br><br>
          • This quote is valid for 30 days from the date above<br>
          • Prices are subject to change without notice<br>
          • A 50% deposit may be required to begin work<br>
          • Final pricing may vary based on actual work performed<br>
          • Additional charges may apply for work outside the scope described above
        </div>
        
        <div class="footer">
          <p>Thank you for considering our services. We look forward to working with you!</p>
          <p><strong>Questions?</strong> Contact us at ${businessToUse?.phone || ''} or ${businessToUse?.email || ''}</p>
        </div>
      </body>
      </html>
    `;
    
    // Generate PDF filename
    const customerFileName = (dataToUse.customerName || 'Customer')
      .replace(/[^a-z0-9\s]/gi, '')  // Remove special characters
      .trim()
      .replace(/\s+/g, '_');  // Replace spaces with underscores
    
    const pdfFileName = `${customerFileName}_Quote.pdf`;
    
    // Create a temporary iframe to render the HTML and trigger print
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    document.body.appendChild(printFrame);
    
    const printDocument = printFrame.contentWindow.document;
    printDocument.open();
    printDocument.write(htmlContent);
    printDocument.close();
    
    // Set the document title for the PDF filename
    printDocument.title = pdfFileName;
    
    // Wait for content to load, then trigger print
    setTimeout(() => {
      printFrame.contentWindow.focus();
      printFrame.contentWindow.print();
      
      // Clean up after a delay
      setTimeout(() => {
        document.body.removeChild(printFrame);
      }, 1000);
    }, 500);
    
  } catch (error) {
    console.error('Error generating PDF:', error);
    alert('Failed to generate PDF. Please try again.');
  }
};

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fff' }}>
      <div style={styles.container}>
        {/* Customer search/select */}
        <label style={styles.label}>Customer Name</label>
        <input
          style={styles.input}
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Search or add customer"
          autoComplete="off"
          autoCorrect="off"
        />
        {searchQuery ? (
          <div style={styles.searchResults}>
            {filteredCustomers.length > 0 ? (
              filteredCustomers.map((customer, index) => (
                <div
                  key={customer.id || `c-${index}`}
                  onClick={() => {
                    setCustomerName(customer.name);
                    setSearchQuery(customer.name);
                    setSelectedCustomer(customer.name);
                    setFilteredCustomers([]);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <div style={styles.searchResult}>{customer.name}</div>
                </div>
              ))
            ) : !customers.some((c) => c.name === searchQuery) ? (
              <div style={styles.addCustomerRow}>
                <div style={styles.searchResult}>{searchQuery}</div>
                <div
                  style={styles.addCustomerButton}
                  onClick={() => {
                    setNewCustomer((nc) => ({ ...nc, name: searchQuery }));
                    setAddingCustomer(true);
                  }}
                >
                  <MdAdd size={20} color="green" />
                  <span style={styles.addCustomerText}>Add Customer</span>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Inline "Add Customer" form */}
        {addingCustomer && (
          <div style={styles.inlineModal}>
            <h3 style={styles.modalHeader}>Add Customer</h3>
            {[
              ['Customer Name', 'name'],
              ['Address', 'address'],
              ['City', 'city'],
              ['State', 'state'],
              ['Zip', 'zip'],
              ['Phone', 'phone'],
              ['Email', 'email'],
              ['Notes', 'notes'],
            ].map(([label, key]) => (
              <div key={key} style={{ marginBottom: 10 }}>
                <label style={styles.modalLabel}>{label}</label>
                <input
                  style={styles.input}
                  value={newCustomer[key]}
                  onChange={(e) => {
                    // Only allow numbers, spaces, dashes, parentheses for phone field
                    if (key === 'phone') {
                      const filtered = e.target.value.replace(/[^0-9\s\-()]/g, '');
                      setNewCustomer((nc) => ({ ...nc, [key]: filtered }));
                    } else {
                      setNewCustomer((nc) => ({ ...nc, [key]: e.target.value }));
                    }
                  }}
                  placeholder={label}
                />
              </div>
            ))}
            <button style={styles.saveButton} onClick={handleAddCustomer}>
              Save Customer
            </button>
            <button style={styles.returnButton} onClick={() => setAddingCustomer(false)}>
              Return To Quote
            </button>
          </div>
        )}

                {/* Quote fields */}
                <label style={styles.label}>Title</label>
        <input 
          style={styles.input} 
          value={title} 
          onChange={(e) => setTitle(e.target.value)}
        />

        <label style={styles.label}>Description</label>
        <textarea
          style={{...styles.input, ...styles.textArea}}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />

        <label style={styles.label}>Price</label>
        <input
          style={styles.input}
          type="text"
          inputMode="decimal"
          value={price}
          onChange={(e) => {
            // Only allow numbers and decimal point
            const cleanedText = e.target.value.replace(/[^0-9.]/g, '');
            setPrice(cleanedText);
          }}
          placeholder="0.00"
        />

        {/* Notes Section */}
        {!isNotesVisible ? (
          <div style={styles.addNotesButton} onClick={() => setIsNotesVisible(true)}>
            <MdAdd size={20} color="green" />
            <span style={styles.addNotesText}>Add Notes</span>
          </div>
        ) : (
          <>
            <div style={styles.hideNotesButton} onClick={() => setIsNotesVisible(false)}>
              <MdRemove size={20} color="red" />
              <span style={styles.hideNotesText}>Hide Notes</span>
            </div>
            <div style={styles.fieldContainer}>
              <label style={styles.label}>Notes (for internal use only):</label>
              <textarea
                style={{...styles.input, ...styles.textArea}}
                placeholder="Enter notes here..."
                onChange={(e) => setNotes(e.target.value)}
                value={notes}
              />
            </div>
          </>
        )}

        <button style={styles.saveButton} onClick={saveQuote}>
          Save Quote
        </button>
        <button
          style={styles.cancelButton}
          onClick={() => navigate('/quotes', { replace: true })}
        >
          Cancel
        </button>
      </div>

      {/* Preview overlay */}
      {previewOpen && (
        <div style={overlayStyles.overlay}>
          <div style={overlayStyles.card}>
            <iframe
              srcDoc={`
                <!DOCTYPE html>
                <html>
                <head>
                  <meta charset="UTF-8">
                  <meta name="viewport" content="width=device-width, initial-scale=1.0">
                  <title>Quote Q-${lastCreatedQuoteId || 'XXXX'} - ${previewQuoteData.customerName || 'Customer'}</title>
                  <style>
                    @media print {
                      body { margin: 0; }
                      .action-buttons { display: none; }
                    }
                    
                    body {
                      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                      line-height: 1.6;
                      color: #333;
                      max-width: 800px;
                      margin: 0 auto;
                      padding: 20px 0px 60px 0px;
                      background: #fff;
                    }
                    
                    .header {
                      display: flex;
                      justify-content: space-between;
                      align-items: flex-start;
                      margin-bottom: 40px;
                      padding-bottom: 20px;
                      border-bottom: 3px solid #2563eb;
                      margin-top: 20px;
                    }
                    
                    .logo-container {
                      margin-bottom: 15px;
                    }
                    
                    .business-logo {
                      max-width: 200px;
                      max-height: 100px;
                      object-fit: contain;
                    }
                    
                    .logo-placeholder {
                      font-size: 11px;
                      font-style: italic;
                      color: #999;
                      margin-bottom: 10px;
                    }
                    
                    .company-info {
                      flex: 1;
                    }
                    
                    .company-name {
                      font-size: 24px;
                      font-weight: bold;
                      color: #2563eb;
                      margin-bottom: 10px;
                    }
                    
                    .company-details {
                      color: #666;
                      font-size: 14px;
                    }
                    
                    .quote-info {
                      text-align: right;
                      flex: 1;
                    }
                    
                    .quote-title {
                      font-size: 32px;
                      font-weight: bold;
                      color: #2563eb;
                      margin-bottom: 10px;
                    }
                    
                    .quote-details {
                      font-size: 14px;
                      color: #666;
                    }
                    
                    .customer-section {
                      margin-bottom: 30px;
                    }
                    
                    .section-title {
                      font-size: 18px;
                      font-weight: bold;
                      color: #374151;
                      margin-bottom: 15px;
                      padding-bottom: 5px;
                      border-bottom: 2px solid #e5e7eb;
                    }
                    
                    .customer-info {
                      background: #f8fafc;
                      padding: 20px;
                      border-radius: 8px;
                      border-left: 4px solid #2563eb;
                    }
                    
                    .customer-name {
                      font-size: 20px;
                      font-weight: bold;
                      color: #1f2937;
                      margin-bottom: 10px;
                    }
                    
                    .services-section {
                      margin-bottom: 30px;
                    }
                    
                    .service-item {
                      background: #fff;
                      border: 1px solid #e5e7eb;
                      border-radius: 8px;
                      padding: 25px;
                      margin-bottom: 20px;
                      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                    }
                    
                    .service-title {
                      font-size: 20px;
                      font-weight: bold;
                      color: #1f2937;
                      margin-bottom: 15px;
                    }
                    
                    .service-description {
                      color: #4b5563;
                      margin-bottom: 15px;
                      line-height: 1.7;
                      white-space: pre-wrap;
                    }
                    
                    .pricing-section {
                      background: #f8fafc;
                      border-radius: 8px;
                      padding: 25px;
                      margin-bottom: 30px;
                      border: 2px solid #2563eb;
                    }
                    
                    .price-row {
                      display: flex;
                      justify-content: space-between;
                      align-items: center;
                      padding: 15px 0;
                      border-bottom: 1px solid #e5e7eb;
                    }
                    
                    .price-row:last-child {
                      border-bottom: none;
                      font-size: 24px;
                      font-weight: bold;
                      color: #2563eb;
                      margin-top: 10px;
                      padding-top: 20px;
                      border-top: 2px solid #2563eb;
                    }
                    
                    .price-label {
                      font-size: 18px;
                      color: #374151;
                    }
                    
                    .price-amount {
                      font-size: 18px;
                      font-weight: bold;
                      color: #1f2937;
                    }
                    
                    .total-label {
                      font-size: 20px;
                      font-weight: bold;
                    }
                    
                    .total-amount {
                      font-size: 24px;
                      font-weight: bold;
                      color: #2563eb;
                    }
                    
                    .terms-section {
                      margin-top: 40px;
                      padding: 20px;
                      background: #fefce8;
                      border-radius: 8px;
                      border-left: 4px solid #eab308;
                    }
                    
                    .terms-title {
                      font-size: 16px;
                      font-weight: bold;
                      color: #92400e;
                      margin-bottom: 10px;
                    }
                    
                    .terms-text {
                      font-size: 14px;
                      color: #a16207;
                      line-height: 1.6;
                    }
                    
                    .footer {
                      margin-top: 50px;
                      padding-top: 20px;
                      border-top: 2px solid #e5e7eb;
                      text-align: center;
                      color: #6b7280;
                      font-size: 14px;
                    }
                  </style>
                </head>
                <body>
                  <div class="header">
                    <div class="company-info">
                      <div class="logo-container">
                        ${businessLogoUrl 
                          ? `<img src="${businessLogoUrl}" alt="Business Logo" class="business-logo" />` 
                          : '<div class="logo-placeholder">Your business logo will appear here once uploaded by Owner/Admin in Settings.</div>'
                        }
                      </div>
                      <div class="company-name">${businessInfo?.businessName || 'Your Business Name'}</div>
                      <div class="company-details">
                        ${(() => {
                          if (!businessInfo) {
                            return 'Business contact info will be blank on quote until set by Owner/Admin in Settings.';
                          }
                          
                          const addressParts = [];
                          if (businessInfo.firstName || businessInfo.lastName) {
                            const fullName = [businessInfo.firstName, businessInfo.lastName].filter(Boolean).join(' ');
                            addressParts.push(fullName);
                          }
                          if (businessInfo.address) addressParts.push(businessInfo.address);
                          if (businessInfo.city && businessInfo.state) {
                            const cityState = businessInfo.zip ? `${businessInfo.city}, ${businessInfo.state} ${businessInfo.zip}` : `${businessInfo.city}, ${businessInfo.state}`;
                            addressParts.push(cityState);
                          }
                          if (businessInfo.phone) {
                            addressParts.push(`Phone: ${businessInfo.phone}`);
                          }
                          if (businessInfo.email) {
                            addressParts.push(`Email: ${businessInfo.email}`);
                          }
                          
                          return addressParts.join('<br>') || 'Contact information must be set by Owner/Admin in Settings.';
                        })()}
                      </div>
                    </div>
                    <div class="quote-info">
                      <div class="quote-title">QUOTE</div>
                      <div class="quote-details">
                        <strong>Quote #:</strong> Q-${lastCreatedQuoteId || 'XXXX'}<br>
                        <strong>Date:</strong> ${new Date().toLocaleDateString()}<br>
                        <strong>Valid Until:</strong> ${new Date(Date.now() + 30*24*60*60*1000).toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  <div class="customer-section">
                    <div class="section-title">Quote For:</div>
                    <div class="customer-info">
                      <div class="customer-name">${previewQuoteData.customerName || 'Customer Name'}</div>
                      ${(() => {
                        const customer = customers.find(c => c.name === previewQuoteData.customerName);
                        if (customer) {
                          const parts = [];
                          if (customer.address) parts.push(`<div>${customer.address}</div>`);
                          if (customer.city && customer.state) parts.push(`<div>${customer.city}, ${customer.state} ${customer.zip || ''}</div>`);
                          if (customer.phone) parts.push(`<div>Phone: ${customer.phone}</div>`);
                          if (customer.email) parts.push(`<div>Email: ${customer.email}</div>`);
                          return parts.join('');
                        }
                        return '';
                      })()}
                    </div>
                  </div>

                  <div class="services-section">
                    <div class="section-title">Services</div>
                    <div class="service-item">
                      <div class="service-title">${previewQuoteData.title || 'Service Title'}</div>
                      <div class="service-description">${previewQuoteData.description || 'No details provided.'}</div>
                    </div>
                  </div>

                  <div class="pricing-section">
                    <div class="price-row">
                      <span class="price-label">Service Total:</span>
                      <span class="price-amount">$${parseFloat(previewQuoteData.price || 0).toFixed(2)}</span>
                    </div>
                    <div class="price-row">
                      <span class="total-label">Total Amount:</span>
                      <span class="total-amount">$${parseFloat(previewQuoteData.price || 0).toFixed(2)}</span>
                    </div>
                  </div>

                  <div class="terms-section">
                    <div class="terms-title">Quote Terms & Conditions</div>
                    <div class="terms-text">
                      • This quote is valid for 30 days from the date above<br>
                      • Prices are subject to change without notice<br>
                      • A 50% deposit may be required to begin work<br>
                      • Final pricing may vary based on actual work performed<br>
                      • Additional charges may apply for work outside the scope described above
                    </div>
                  </div>

                  <div class="footer">
                    <p>Thank you for considering our services. We look forward to working with you!</p>
                    <p><strong>Questions?</strong> Contact us at ${businessInfo?.phone || 'Company Phone'} or ${businessInfo?.email || 'Company Email'}</p>
                  </div>
                </body>
                </html>
              `}
              style={{
                width: '100%',
                height: '80vh',
                border: 'none',
                borderRadius: '8px'
              }}
              title="Quote Preview"
            />
            
            <div style={overlayStyles.modalButtons}>
              <button 
                style={{...overlayStyles.modalButton, ...overlayStyles.emailButton}}
                onClick={async () => {
                  const customerData = customers.find(
                    (c) => (c?.name ?? '').trim().toLowerCase() === (previewQuoteData.customerName || '').trim().toLowerCase()
                  );
                  
                  if (!customerData?.email) {
                    showCustomModal(
                      'Email Not Available ⚠️',
                      'Customer email not found. Please update customer information.',
                      [{ text: 'OK', style: 'secondary', onPress: closeCustomModal }]
                    );
                    return;
                  }

                  setPreviewOpen(false);
                  
                  if (lastCreatedQuoteId) {
                    try {
                      setSendingEmail(true);
                      
                      showCustomModal(
                        'Sending Email...',
                        'Please wait while we send your quote.',
                        [
                          {
                            text: 'Sending…',
                            style: 'primary',
                            disabled: true,
                            onPress: () => {}
                          }
                        ]
                      );

                      const token = getToken();
                      const emailResponse = await axios.post(`${API_BASE_URL}/api/quotes/${lastCreatedQuoteId}/email`, {}, {
                        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                      });

                      if (emailResponse.data && emailResponse.data.success) {
                        showCustomModal(
                          'Email Sent Successfully! ✅',
                          `Quote has been emailed as PDF to ${customerData.email}`,
                          [{ text: 'Great!', style: 'primary', onPress: closeCustomModal }]
                        );
                      } else {
                        showCustomModal(
                          'Email Status Unclear ⚠️',
                          emailResponse.data?.message || 'Email may have been sent, but status is unclear.',
                          [{ text: 'OK', style: 'secondary', onPress: closeCustomModal }]
                        );
                      }
                    } catch (emailError) {
                      console.error('Error sending quote email:', emailError);
                      
                      let errorMessage = 'Failed to send email. Please try again.';
                      if (emailError.response?.status === 400) {
                        errorMessage = 'Customer email issue. Please update customer information.';
                      } else if (emailError.response?.data?.detail) {
                        errorMessage = emailError.response.data.detail;
                      }
                      
                      showCustomModal(
                        'Email Failed ❌',
                        errorMessage,
                        [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
                      );
                    } finally {
                      setSendingEmail(false);
                    }
                  }
                }}
                disabled={sendingEmail}
              >
                {sendingEmail ? 'Sending…' : '📧 One-Touch Email'}
              </button>

              <button 
                style={{...overlayStyles.modalButton, ...overlayStyles.savePdfButton}}
                onClick={() => generateAndSharePDF(previewQuoteData, lastCreatedQuoteId)}
              >
                💾 Save/Print PDF
              </button>
            </div>

            <button 
              style={overlayStyles.closeLink}
              onClick={() => setPreviewOpen(false)}
            >
              Close Preview
            </button>
          </div>
        </div>
      )}

      {/* Custom Modal */}
      {customModal.open && (
        <div style={overlayStyles.overlay}>
          <div style={overlayStyles.card}>
            <h2 style={{ fontSize: 20, fontWeight: 'bold', marginBottom: 12 }}>{customModal.title}</h2>
            <p>{customModal.message}</p>
            <div style={{ marginTop: 16 }}>
              {customModal.buttons.map((button, index) => (
                <button
                  key={index}
                  style={{
                    backgroundColor: button.style === 'primary' ? 'blue' : button.style === 'secondary' ? 'gray' : button.style === 'danger' ? 'red' : '#f0f0f0',
                    color: button.style === 'default' ? 'black' : 'white',
                    padding: 12,
                    borderRadius: 6,
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    width: '100%',
                    marginBottom: 10,
                    fontSize: 14,
                  }}
                  onClick={button.onPress}
                >
                  {button.text}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: 20, paddingBottom: 50, backgroundColor: '#fff' },
  label: { marginTop: 15, fontWeight: 'bold', display: 'block', fontSize: 16 },
  input: { 
    width: '100%',
    border: '1px solid #ccc', 
    padding: 8, 
    borderRadius: 4,
    boxSizing: 'border-box',
    fontSize: 16
  },
  textArea: { height: 100, resize: 'vertical', fontSize: 16 },
  searchResults: { marginTop: 5, backgroundColor: '#f9f9f9', borderRadius: 4, padding: 10 },
  searchResult: { paddingTop: 5, paddingBottom: 5, fontSize: 14 },
  addCustomerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  addCustomerButton: { display: 'flex', alignItems: 'center', marginLeft: 10, cursor: 'pointer' },
  addCustomerText: { color: 'green', marginLeft: 5, fontSize: 14 },
  saveButton: { 
    marginTop: 20, 
    backgroundColor: 'blue', 
    color: 'white',
    padding: 12, 
    borderRadius: 6, 
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'bold',
    width: '100%',
    fontSize: 16
  },
  cancelButton: { 
    marginTop: 10, 
    backgroundColor: 'red',
    color: 'white', 
    padding: 12, 
    borderRadius: 6,
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'bold',
    width: '100%',
    fontSize: 16
  },
  fieldContainer: { marginTop: 0 },
  addNotesButton: { display: 'flex', alignItems: 'center', marginTop: 15, cursor: 'pointer' },
  addNotesText: { color: 'green', marginLeft: 5, fontSize: 14 },
  hideNotesButton: { display: 'flex', alignItems: 'center', marginTop: 15, cursor: 'pointer' },
  hideNotesText: { color: 'red', marginLeft: 5, fontSize: 14 },
  inlineModal: {
    marginTop: 16,
    padding: 16,
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    backgroundColor: '#fff',
  },
  modalHeader: { fontSize: 18, fontWeight: 'bold', marginBottom: 12 },
  modalLabel: { marginTop: 10, fontWeight: 'bold', fontSize: 14, display: 'block' },
  returnButton: { 
    backgroundColor: '#ccc', 
    padding: 10, 
    borderRadius: 5, 
    border: 'none',
    cursor: 'pointer',
    marginTop: 10,
    width: '100%',
    fontWeight: 'bold',
    fontSize: 16
  },
};

const overlayStyles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    zIndex: 1000,
  },
  card: {
    width: 'min(640px, 100%)',
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    border: '1px solid #e5e7eb',
  },
  modalButtons: {
    display: 'flex',
    flexDirection: 'row',
    gap: 10,
    marginTop: 20,
  },
  modalButton: {
    flex: 1,
    padding: 12,
    borderRadius: 6,
    textAlign: 'center',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: 14,
  },
  emailButton: {
    backgroundColor: 'green',
    color: 'white',
  },
  savePdfButton: {
    backgroundColor: 'blue',
    color: 'white',
  },
  closeLink: {
    width: '100%',
    textAlign: 'center',
    marginTop: 12,
    padding: 8,
    color: '#FF3B30',
    fontSize: 16,
    textDecoration: 'underline',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
  },
};
